import React from "react"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@heroui/react"
import {
  IconAlertTriangle,
  IconMail,
  IconExternalLink,
} from "@tabler/icons-react"

interface CapacityLimitModalProps {
  isOpen: boolean
  onClose: () => void
  currentCount: number
  maxCount: number
  context: "archiving" | "auth"
}

export function CapacityLimitModal({
  isOpen,
  onClose,
  currentCount,
  maxCount,
  context,
}: CapacityLimitModalProps) {
  const getContextualCopy = () => {
    if (context === "auth") {
      return {
        title: "Authentication Capacity Reached",
        subtitle: "You've reached your plan's authentication limit",
        categoryName: "auth",
        description: `To connect additional authentication integrations for user authentication and directory management, you'll need to upgrade your plan. Our sales team can help you find the right solution for your organization's identity management needs.`,
      }
    } else {
      return {
        title: "Archiving Capacity Reached",
        subtitle: "You've reached your plan's archiving integration limit",
        categoryName: "archiving",
        description: `To connect additional archiving integrations for data archiving, you'll need to upgrade your plan. Our sales team can help you find the right solution for your organization's archiving and compliance needs.`,
      }
    }
  }

  const handleContactSales = () => {
    const contextText = context === "auth" ? "Authentication" : "Archiving"
    window.open(
      `mailto:ben@obsidianlabs.co?subject=${contextText} Integration Capacity Upgrade Request`,
      "_blank"
    )
    onClose()
  }

  const copy = getContextualCopy()

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="md"
      classNames={{
        base: "bg-white",
        backdrop: "bg-black/50",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-2 pb-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
              <IconAlertTriangle size={20} className="text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {copy.title}
              </h3>
              <p className="text-sm text-gray-500">{copy.subtitle}</p>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="py-4">
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Current {copy.categoryName} integrations
                </p>
                <p className="text-xs text-gray-500">
                  You're using all available integration slots
                </p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-gray-900">
                  {currentCount}/{maxCount}
                </span>
                <p className="text-xs text-gray-500">integrations</p>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-sm text-gray-600">{copy.description}</p>
          </div>
        </ModalBody>

        <ModalFooter className="gap-2">
          <Button variant="flat" onPress={onClose} className="text-gray-600">
            Maybe Later
          </Button>
          <Button
            color="primary"
            onPress={handleContactSales}
            startContent={<IconMail size={16} />}
            endContent={<IconExternalLink size={14} />}
          >
            Contact Sales
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
